import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { IChatKeyword } from '../../libs/models/conversations/ChatKeyword';
import { INewsCardArticle } from '../../libs/models/conversations/ChatNewsCard';
import { toNewsCardArticles } from '../../libs/models/conversations/ChatNewsCardMapper';
import { NewsItem } from '../../libs/models/users/ChatNews';
import { NewsService } from '../../libs/services/NewsService';
import { ChatCardNews } from './ChatCardNews';

interface ChatItTrendProps {
  isDisabled: boolean;
  sendMessage: (item: IChatKeyword) => void;
}

interface TrendItem {
  content: string;
  summary?: string;
  isRecommend: boolean;
  source?: IChatKeyword;
  // 아이템 클릭 시 노출할 뉴스 카드 데이터
  article?: INewsCardArticle;
}

// IT Trend 자동 롤링 간격 (ms)
const ROLL_INTERVAL_MS = 4000;

export const ChatItTrend: React.FC<ChatItTrendProps> = ({ isDisabled, sendMessage }) => {
  const newsService = useMemo(() => new NewsService(), []);

  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [reportDate, setReportDate] = useState('');

  const articles = useMemo(() => toNewsCardArticles(newsItems), [newsItems]);
  const items: TrendItem[] = useMemo(
    () =>
      newsItems.map((news, index) => ({
        content: news.displayTitle,
        summary: news.detailTitle,
        isRecommend: false,
        article: articles[index],
      })),
    [newsItems, articles],
  );

  useEffect(() => {
    let isActive = true;

    const load = async () => {
      try {
        const result = await newsService.getNews();
        if (!isActive) return;

        setNewsItems(result.news);
        setReportDate(result.reportDate);
      } catch {
        if (!isActive) return;

        setNewsItems([]);
        setReportDate('');
      }
    };

    void load();

    return () => {
      isActive = false;
    };
  }, [newsService]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [activeArticleIndex, setActiveArticleIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const sizerRef = useRef<HTMLDivElement | null>(null);
  // 직전에 열람 기록을 보낸 기사 id. 같은 기사의 중복 기록을 막는다.
  const lastViewedIdRef = useRef<string | null>(null);
  const [listWidth, setListWidth] = useState<number | null>(null);

  const step = (direction: number) => {
    setActiveIndex((prev) => (prev + direction + items.length) % items.length);
  };

  // 조회 결과가 바뀌면 첫 번째 항목부터 다시 본다.
  useEffect(() => {
    setActiveIndex(0);
  }, [items.length]);

  /**
   * 카드뉴스를 열고 열람 기록을 남긴다.
   */
  const openArticle = (index: number) => {
    setActiveArticleIndex(index);

    const news = newsItems[index];
    if (!news || !reportDate) return;
    if (lastViewedIdRef.current === news.id) return;

    lastViewedIdRef.current = news.id;
    void newsService.postNewsView({ reportDate, articleId: news.id }).catch(() => {
      // 열람 기록 실패는 무시한다.
    });
  };

  // 자동 롤링: 호버 중이거나 아이템이 1개 이하거나 뉴스 카드가 열려있으면 정지
  useEffect(() => {
    if (isPaused || items.length <= 1 || activeArticleIndex !== null) return;
    const timer = setInterval(() => {
      step(1);
    }, ROLL_INTERVAL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [isPaused, items.length, activeArticleIndex]);

  // 가장 긴 아이템 너비를 선호 크기로 잡고, 화면이 좁으면 list가 줄어들게 함
  // 모바일에서는 CSS(.trend-list { width: 100% })가 폭을 맞추도록 인라인 width를 적용 X
  useLayoutEffect(() => {
    const el = sizerRef.current;
    if (!el) return;

    const measure = () => {
      if (window.matchMedia('(max-width: 768px)').matches) {
        setListWidth(null);
        return;
      }
      const maxWidth = Math.ceil(
        Math.max(0, ...Array.from(el.children).map((child) => (child as HTMLElement).getBoundingClientRect().width)),
      );
      if (maxWidth > 0) setListWidth(maxWidth);
    };

    measure();
    window.addEventListener('resize', measure);
    const retryId = window.setTimeout(measure, 0);

    return () => {
      window.removeEventListener('resize', measure);
      window.clearTimeout(retryId);
    };
  }, [items]);

  // 마우스 스크롤(휠)로 앞/뒤 아이템을 직접 넘길 수 있게 처리
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (items.length <= 1) return;
      e.preventDefault();
      step(e.deltaY > 0 ? 1 : -1);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
    };
  }, [items.length]);

  // 가운데 칸은 항상 현재 뉴스, 위/아래는 이전·다음. 롤링 시 제목이 가운데를 지나감
  const wrapIndex = (offset: number) => (activeIndex + offset + items.length) % items.length;
  const visibleItems =
    items.length === 1
      ? [{ item: items[0], index: 0, slot: 0 }]
      : [-2, -1, 0, 1].map((slot) => {
          const index = wrapIndex(slot);
          return { item: items[index], index, slot };
        });

  // 조회된 뉴스가 없으면 영역 자체를 숨긴다.
  if (items.length === 0) return null;

  return (
    <>
      <div className="trend-card">
        <div className="trend-label">
          <h2 className="trend-title">
            AX Trend
          </h2>
          <p className="trend-counter">
            {activeIndex + 1} / {items.length}
          </p>
        </div>

        <div
          className="trend-list"
          ref={listRef}
          style={listWidth ? { width: `${listWidth}px` } : undefined}
          onMouseEnter={() => {
            setIsPaused(true);
          }}
          onMouseLeave={() => {
            setIsPaused(false);
          }}
        >
          <span className="trend-fade-top" />
          <div className="trend-track" key={activeIndex}>
          {visibleItems.map(({ item, index, slot }) => (
            <a
              key={slot}
              href="#"
              role="button"
              data-slot={slot}
              className={classNames('trend-item', { active: index === activeIndex, disabled: isDisabled })}
              onClick={(e) => {
                e.preventDefault();
                if (isDisabled) return;
                if (item.article) {
                  openArticle(index);
                  return;
                }
                if (item.source) sendMessage(item.source);
              }}
            >
              {index === activeIndex && <span className="trend-focus-mark" />}
              <p className="trend-headline">{item.content}</p>
              {index === activeIndex && item.summary && <p className="trend-desc">{item.summary}</p>}
            </a>
          ))}
          </div>
          <span className="trend-fade-bottom" />
        </div>
        <div className="trend-sizer" ref={sizerRef} aria-hidden="true">
          {items.map((item, index) => (
            <div key={index} className="trend-item active">
              <p className="trend-headline">{item.content}</p>
            </div>
          ))}
        </div>
      </div>

      {/* IT Trend 아이템 클릭 시 노출되는 카드뉴스 */}
      {activeArticleIndex !== null && articles.length > 0 && (
        <ChatCardNews
          articles={articles}
          initialIndex={activeArticleIndex}
          onIndexChange={(index) => {
            // 카드 안에서 좌우 이동할 때도 해당 기사의 열람을 기록한다.
            openArticle(index);
          }}
          onClose={() => {
            setActiveArticleIndex(null);
            // 다음에 다시 열면 같은 기사라도 새 열람으로 기록되도록 초기화한다.
            lastViewedIdRef.current = null;
          }}
        />
      )}
    </>
  );
};
